// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import Button from '@/components/button';
import Breadcrumbs from '@/components/breadcrumbs';
import Search from '@/components/search';
import KendoPager from '@/components/kendo/kendo-pager';
import Router from 'next/router';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import DownloadDropdown from '@/components/download-dropdown';
import PS from '@/services/permission/permission-service';
import KendoTable from '@/components/kendo/kendo-table';
import NoSSR from '@/utils/no-ssr'
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router'



const usePopulatedList = false;

const BASE_COLUMNS = [{
  Label: 'Customer',
  ColumnName: 'CustomerName',
  CellType: 'none',
}, {
  Label: 'Contact',
  ColumnName: 'CustomerContactFullName',
  CellType: 'none',
}, {
  Label: 'Location',
  ColumnName: 'LocationDisplay',
  CellType: 'none',
}, {
  Label: 'Service',
  ColumnName: 'InventoryDescription',
  CellType: 'none',
}, {
  Label: 'Job Type',
  ColumnName: 'JobTypeName',
  CellType: 'none',
}, {
  Label: 'Last Run Date',
  ColumnName: 'LastRunDate',
  CellType: 'date',
}, {
  Label: 'Next Run Date',
  ColumnName: 'NextRunDate',
  CellType: 'date',
}, {
  Label: 'Created',
  ColumnName: 'CreatedDate',
  CellType: 'date',
}, {
  Label: 'Created By',
  ColumnName: 'CreatedBy',
  CellType: 'none',
}, {
  Label: 'Active',
  ColumnName: 'IsActive',
  CellType: 'icon',
}];

const ANCILLARY_FILTERS = {
  IncludeDisabled: [{
    type: Enums.ControlType.Switch,
    label: 'Include disabled jobs',
  }],
};

function JobScheduleList() {

  const [addRecurringJobPermission] = useState(PS.hasPermission(Enums.PermissionName.RecurringJob));
  const [exportPermission] = useState(PS.hasPermission(Enums.PermissionName.Exports));

  const [data, setData] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [searchVal, setSearchVal] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [ancillaryFilters, setAncillaryFilters] = useState({ IncludeDisabled: false });

  const handleAncillaryFilterChange = (result: any) => {
    if (result.reset) {
      setAncillaryFilters({ IncludeDisabled: false });
    } else {
      if (result.key == "IncludeDisabled") {
        setAncillaryFilters({ IncludeDisabled: result.checked });
      }
    }
  };

  const { data: serviceModeData } = useQuery(
    ['isServiceMode'],
    () => Fetch.get({ url: '/Option/GetByOptionName?name=Job Service' })
  );

  const isServiceMode = serviceModeData ? serviceModeData.toLowerCase() === 'true' : false;

  const [columnState, setColumnState] = useState(BASE_COLUMNS);

  useEffect(() => {
    if (serviceModeData !== undefined) {
      const mode = serviceModeData ? serviceModeData.toLowerCase() === 'true' : false;
      setColumnState(mode ? BASE_COLUMNS : BASE_COLUMNS.filter((x: any) => x.Label !== 'Service'));
    }
  }, [serviceModeData]);

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  }

  const [pageSize, setPageSize] = useState(10);

  const pageSizeChanged = (size: any) => {
    setPageSize(size);
  };

  const pageChanged = (page: any) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  const columns = React.useMemo(
    () => columnState.map(function (column: any) {
      return Helper.getColumnObject(column);
    }),
    [columnState]
  );

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      searchJobSchedules();
      return;
    }

    if (currentPage == 1) {
      searchJobSchedules();
    } else {
      setCurrentPage(1);
    }
  }, [searchVal, sortField, sortDirection, pageSize, ancillaryFilters]);

  const firstUpdatePage = useRef(true);
  useEffect(() => {
    if (firstUpdatePage.current) {
      firstUpdatePage.current = false;
      return;
    }
    searchJobSchedules();
  }, [currentPage]);

  function rowClick(row: any) {
    Helper.nextRouter(Router.push, '/job-schedule/[id]', '/job-schedule/' + row.original.ID);
  }

  function setSort(field: any) {
    if (field != sortField) {
      setSortDirection('ascending');
    } else {
      if (sortDirection == 'ascending') {
        setSortDirection('descending');
      } else {
        setSortDirection('ascending');
      }
    }

    setSortField(field);
  }

  async function searchJobSchedules() {

    setSearching(true);

    const searchRes = await Fetch.post({
      url: '/JobSchedule/GetJobSchedules',
      params: {
        PageSize: pageSize,
        PageIndex: (currentPage - 1),
        SearchPhrase: searchVal,
        SortExpression: sortField,
        SortDirection: sortDirection,
        IncludeClosed: ancillaryFilters["IncludeDisabled"],
        PopulatedList: usePopulatedList,
      },
    });

    setData(searchRes.Results);
    setTotalResults(searchRes.TotalResults);
    setSearching(false);
  }

  const createJobSchedule = () => {
    Helper.nextRouter(Router.push, "/job-schedule/create");
  };

  const [params, setParams] = useState<any>({});

  // for table resize columns
  async function onColumnResize(columnName: any, width: any, columnNamesAndWidths: any) {
    const newState = columnState.map(function (column: any) {
      if (column.ColumnName === columnName) {
        column.UserWidth = width;
      } else if (columnNamesAndWidths) {
        let match = columnNamesAndWidths.find((x: any) => x.columnName === column.ColumnName);
        if (match) {
          column.UserWidth = match.width;
        }
      }
      return column;
    });
    setColumnState(newState);
  }
  // for table resize columns

  return (
    <div className="list-container">
      <div className="row">
        <div className="title">
          <Breadcrumbs currPage={{ text: 'Recurring Job List', link: '/job-schedule/list', type: 'list' }} />
        </div>

        <NoSSR>
          <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess || !addRecurringJobPermission}
            text="Create" icon="plus-circle" extraClasses="fit-content no-margin" onClick={createJobSchedule} />
        </NoSSR>        
      </div>

      <div className="row end padded">
        <div className="search-container">
          <Search
            placeholder="Search customer, contact or location"
            resultsNum={data.length}
            searchVal={searchVal}
            ancillaryFilters={ANCILLARY_FILTERS}
            setAncillaryFilters={handleAncillaryFilterChange}
            setSearchVal={setSearchVal}
          />
        </div>
        <div className="row flexEnd">
          {accessStatus !== Enums.AccessStatus.LockedWithAccess && accessStatus !== Enums.AccessStatus.LockedWithOutAccess && exportPermission ? <>
            <div className="download">
              <DownloadDropdown title={'Export'} options={[
                { url: '/JobSchedule/GetExportedJobs', method: 'POST', params: { ExportAll: true }, label: 'Export' },
              ]} />
            </div>
          </> : ''}
        </div>
      </div>

      <div className={"no-items" + (data.length == 0 ? " no-items-visible" : "")}>
        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>
        <img src="/job-folder.svg" alt="Job Folder" />
        <h3>No recurring jobs found</h3>
        <p>If you can't find a job, try another search or create a new one.</p>
        <a href="/job-schedule/create"><img src="/icons/plus-circle-blue.svg" alt="plus" /> Add new recurring job</a>
        <img className="wave" src="/wave.svg" alt="wave" />
      </div>

      {data.length != 0 ? <KendoTable
        searching={searching}
        actions={[
          { text: "Edit", icon: "edit", function: (row: any) => Helper.nextRouter(Router.push, `/job-schedule/${row.ID}`) },
        ]}
        columns={columns}
        data={data}
        rowClick={rowClick}
        setSort={setSort}
        sortField={sortField}
        sortDirection={sortDirection}
        heightOffset={295}
        highlightColumnName="CustomerName"
        onColumnResize={onColumnResize}
      /> : ""}

      <KendoPager pageSizeChanged={pageSizeChanged} pageChanged={pageChanged} totalResults={totalResults} searchValue={searchVal}  parentPageNumber={currentPage}/>

      <style jsx>{`
        .row {
          display: flex;
          justify-content: space-between;
        }
        .search-container :global(.search) {
          width: 528px;
        }
        a {
          text-decoration: none;
        }
        .padded {
          padding-bottom: 1rem;
        }
        .flexEnd {
          align-items: flex-end;
        }
        .download {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}

export default JobScheduleList

export const Route = createFileRoute('/_authenticated/job-schedule/list')({ component: JobScheduleList })
